import { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Search, X, ChevronLeft, ChevronRight, Filter, AlertTriangle, Shield, Clock, Activity, HelpCircle } from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import auditLogTutorial from '../components/tutorials/configs/auditLogTutorial';

async function api(path) {
  const res = await fetch(path, { credentials: 'include' });
  return res.json();
}

const ACTION_COLORS = {
  LOGIN_FAILED: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
  ACCOUNT_LOCKED: { bg: 'rgba(239,68,68,0.2)', color: '#ef4444' },
  LOGIN_SUCCESS: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e' },
  LOGIN: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e' },
  ACCESS_REVIEW_COMPLETED: { bg: 'rgba(0,212,170,0.15)', color: 'var(--accent)' },
  ACCOUNT_UNLOCKED: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6' },
};

function ActionBadge({ action }) {
  const style = ACTION_COLORS[action] || { bg: 'rgba(107,114,128,0.15)', color: '#94a3b8' };
  return (
    <span style={{ background: style.bg, color: style.color, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {action}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <Icon size={20} style={{ color }} />
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</div>
      </div>
    </div>
  );
}

export default function AuditLogViewer() {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(auditLogTutorial);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [stats, setStats] = useState(null);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [page, setPage] = useState(1);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', '50');
    if (filterAction) params.set('action', filterAction);
    if (filterSearch) params.set('search', filterSearch);
    if (filterStartDate) params.set('startDate', filterStartDate);
    if (filterEndDate) params.set('endDate', filterEndDate);
    return params.toString();
  }, [page, filterAction, filterSearch, filterStartDate, filterEndDate]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/api/audit-log?${buildQuery()}`);
      if (data.success) {
        setLogs(data.logs);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Failed to load audit logs', err);
    }
    setLoading(false);
  }, [buildQuery]);

  const loadMeta = useCallback(async () => {
    try {
      const [actionsRes, statsRes] = await Promise.all([
        api('/api/audit-log/actions'),
        api('/api/audit-log/stats'),
      ]);
      if (actionsRes.actions) setActionTypes(actionsRes.actions);
      if (statsRes.totalEvents !== undefined) setStats(statsRes);
    } catch (err) {
      console.error('Failed to load meta', err);
    }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (filterAction) params.set('action', filterAction);
    if (filterSearch) params.set('search', filterSearch);
    if (filterStartDate) params.set('startDate', filterStartDate);
    if (filterEndDate) params.set('endDate', filterEndDate);
    window.location.href = `/api/audit-log/csv?${params.toString()}`;
  };

  const clearFilters = () => {
    setFilterAction('');
    setFilterSearch('');
    setFilterStartDate('');
    setFilterEndDate('');
    setPage(1);
  };

  const hasFilters = filterAction || filterSearch || filterStartDate || filterEndDate;

  const inputStyle = {
    background: 'var(--bg-page)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6, padding: '6px 10px', color: '#e2e8f0', fontSize: 13,
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={22} style={{ color: 'var(--accent)' }} />
            Audit Log
          </h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: '4px 0 0' }}>
            Privileged access activity and security event monitoring
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="tutorial-help-btn" onClick={() => startTutorial(auditLogTutorial.module, auditLogTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          <button onClick={handleExportCSV} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
          <StatCard icon={Activity} label={`Events (${stats.periodDays}d)`} value={stats.totalEvents} color="#3b82f6" />
          <StatCard icon={AlertTriangle} label="Failed Logins" value={stats.failedLoginAttempts} color={stats.failedLoginAttempts > 0 ? '#ef4444' : '#22c55e'} />
          <StatCard icon={Shield} label="Top Action" value={stats.topActions?.[0]?.action?.replace(/_/g, ' ') || 'N/A'} color="#00d4aa" />
          <StatCard icon={Clock} label="Top Count" value={stats.topActions?.[0]?.count || 0} color="#94a3b8" />
        </div>
      )}

      {/* Filters */}
      <div style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Filter size={14} style={{ color: 'var(--text-tertiary)' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Filters</span>
          {hasFilters && (
            <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <X size={12} /> Clear all
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 8, top: 9, color: 'var(--text-tertiary)' }} />
            <input
              type="text" placeholder="Search email, action, resource..."
              value={filterSearch} onChange={e => { setFilterSearch(e.target.value); setPage(1); }}
              style={{ ...inputStyle, paddingLeft: 28, width: 240 }}
            />
          </div>
          <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}
            style={{ ...inputStyle, minWidth: 160 }}>
            <option value="">All Actions</option>
            {actionTypes.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <input type="date" value={filterStartDate} onChange={e => { setFilterStartDate(e.target.value); setPage(1); }}
            style={{ ...inputStyle, width: 140 }} />
          <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>to</span>
          <input type="date" value={filterEndDate} onChange={e => { setFilterEndDate(e.target.value); setPage(1); }}
            style={{ ...inputStyle, width: 140 }} />
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              {['Timestamp', 'Tenant', 'User', 'Action', 'Resource', 'IP Address'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>No audit log entries found</td></tr>
            ) : (
              logs.map(log => (
                <tr key={log.id}
                  onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', background: expandedRow === log.id ? 'rgba(0,212,170,0.03)' : 'transparent' }}
                >
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {new Date(log.createdAt).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12 }}>{log.tenantName}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontSize: 12 }}>{log.userEmail || '-'}</div>
                  </td>
                  <td style={{ padding: '10px 12px' }}><ActionBadge action={log.action} /></td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-secondary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.resource || '-'}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                    {log.ipAddress || '-'}
                  </td>
                </tr>
              ))
            )}
            {/* Expanded detail row */}
            {logs.map(log => expandedRow === log.id ? (
              <tr key={`${log.id}-detail`}>
                <td colSpan={6} style={{ padding: '12px 20px', background: 'var(--bg-page)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
                    <div>
                      <span style={{ color: 'var(--text-tertiary)' }}>Event ID: </span>
                      <span style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{log.id}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-tertiary)' }}>User ID: </span>
                      <span style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{log.userId || 'N/A'}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-tertiary)' }}>User Agent: </span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{log.userAgent || 'N/A'}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-tertiary)' }}>Tenant ID: </span>
                      <span style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{log.tenantId}</span>
                    </div>
                    {log.details && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <span style={{ color: 'var(--text-tertiary)' }}>Details: </span>
                        <pre style={{ color: 'var(--text-secondary)', fontSize: 11, margin: '4px 0 0', padding: 10, background: 'var(--bg-page)', borderRadius: 6, overflow: 'auto', maxHeight: 200, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ) : null)}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontSize: 13 }}>
          <span style={{ color: 'var(--text-tertiary)' }}>
            Showing {((page - 1) * 50) + 1}-{Math.min(page * 50, pagination.total)} of {pagination.total} events
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn btn-secondary btn-sm"
              style={{ padding: '4px 8px', opacity: page <= 1 ? 0.4 : 1 }}
            >
              <ChevronLeft size={14} />
            </button>
            <span style={{ padding: '4px 12px', color: 'var(--text-secondary)' }}>Page {page} of {pagination.totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
              className="btn btn-secondary btn-sm"
              style={{ padding: '4px 8px', opacity: page >= pagination.totalPages ? 0.4 : 1 }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={auditLogTutorial.module}
          moduleName={auditLogTutorial.moduleName}
          steps={auditLogTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
}
